import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineSharpIcon from "@mui/icons-material/AddCircleOutlineSharp";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Stack,
  Tab,
  Typography,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDealAndSourcing } from "models/companies/apiCompany";
import DealInfo from "models/deals/DealInfo";
import SourcingInfo from "models/sourcings/SourcingInfo";
import { useModal } from "@ebay/nice-modal-react";

const AddDealSourcingButton = ({
  companyId,
  handleOpenDealForm,
  handleOpenSourcingForm,
}) => {
  const [openAddMenu, setOpenAddMenu] = useState(false);
  const [andchorEl, setAnchorEl] = useState(null);
  const handleOpenAddMenu = (event) => {
    setAnchorEl(!openAddMenu ? event.currentTarget : null);
    setOpenAddMenu(!openAddMenu);
  };

  return (
    <>
      <IconButton onClick={handleOpenAddMenu} sx={{ p: 0.5 }}>
        <AddCircleOutlineSharpIcon sx={{ scale: "0.8" }} />
      </IconButton>
      <Menu
        anchorEl={andchorEl}
        open={openAddMenu}
        onClose={handleOpenAddMenu}
        slotProps={{
          paper: {
            style: {
              width: "30ch",
            },
          },
        }}
      >
        <MenuList>
          <MenuItem
            onClick={() => {
              handleOpenDealForm({
                formType: "create",
                initialValues: {
                  company: companyId,
                },
              });
              handleOpenAddMenu();
            }}
          >
            <ListItemIcon>
              <AddIcon sx={{ scale: "0.8" }} />
            </ListItemIcon>
            <ListItemText>Deal</ListItemText>
            <Typography variant="body2">⌘+d</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleOpenSourcingForm({
                formType: "create",
                initialValues: {
                  company: companyId,
                },
              });
              handleOpenAddMenu();
            }}
          >
            <ListItemIcon>
              <AddIcon sx={{ scale: "0.8" }} />
            </ListItemIcon>
            <ListItemText>Sourcing</ListItemText>
            <Typography variant="body2">⌘+s</Typography>
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
};

const parseDealsAndSourcinsgState = (state) => {
  if (state === undefined) return { type: null, id: null };
  const [type, id] = state ? state.split("-") : [null, null];
  return { type: type, id: parseInt(id) };
};

const convertDealsAndSourcingsToState = (dealsAndSourcings) => {
  if (!dealsAndSourcings) return null;
  return `${dealsAndSourcings.type}-${dealsAndSourcings.id}`;
};

const CompanyDealsAndSourcingsTab = ({
  state,
  handleChange,
  companies,
  onlyActiveDealsAndSourcings,
  widgetMode,
}) => {
  const {
    data: dealsAndSourcings,
    isLoading,
    isError,
  } = useDealAndSourcing(companies, onlyActiveDealsAndSourcings);

  useEffect(() => {
    if (!widgetMode && !state && dealsAndSourcings) {
      handleChange(
        null,
        convertDealsAndSourcingsToState(dealsAndSourcings?.[0]),
      );
    }
  }, [dealsAndSourcings, state]);

  if (isLoading) return <></>;
  if (isError) return <>Error</>;

  return dealsAndSourcings.length > 0 ? (
    <Tabs
      ringstyle="ringapp"
      value={state || false}
      onChange={handleChange}
      sx={{ pb: 2 }}
    >
      {dealsAndSourcings.map((item) => {
        return (
          <Tab
            ringstyle="ringapp"
            key={`${item.type}-${item.id}`}
            value={`${item.type}-${item.id}`}
            label={
              <Stack direction={"column"} spacing={0}>
                {widgetMode && (
                  <Typography variant="subtitle" noWrap sx={{ p: 0.5 }}>
                    {item.company?.best_name}
                  </Typography>
                )}
                <Typography variant="caption" noWrap sx={{ p: 0.5 }}>
                  {`${item.type} - ${item.deal_flow.short_name}`}
                  {` (${dayjs(item.created_at).format("DD/MM/YYYY")})`}
                </Typography>
              </Stack>
            }
            className={item.is_active ? "" : "inactive"}
            wrapped
          />
        );
      })}
    </Tabs>
  ) : (
    <Box display={"flex"} alignItems={"center"}>
      <InfoIcon color="secondary" sx={{ scale: "0.8", mr: 1 }} />
      <Typography variant="caption" sx={{ color: "secondary.main" }}>
        Companies of the note have no active deals or sourcings
      </Typography>
    </Box>
  );
};

const CompanyDealsAndSourcings = ({
  companyIds,
  state,
  setState,
  onlyActiveDealsAndSourcings,
  widgetMode,
}) => {
  const [object, setObject] = useState(parseDealsAndSourcinsgState(state));
  const dealModal = useModal("DealDrawer");
  const sourcingModal = useModal("SourcingDrawer");

  useEffect(() => {
    setObject(parseDealsAndSourcinsgState(state));
  }, [state]);

  useEffect(() => {
    if (widgetMode && object.type) {
      if (object.type == "Deal") {
        dealDrawer.openForm({ objectId: object.id, formType: "update" });
      } else if (object.type == "Sourcing") {
        sourcingDrawer.openForm({ objectId: object.id, formType: "update" });
      } else {
        throw new Error("Invalid object type");
      }
    }
  }, [object, widgetMode]);

  const handleChange = (event, newValue) => {
    if (setState) setState(newValue);
    else setObject(parseDealsAndSourcinsgState(newValue));
  };

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pb: 2 }}
        >
          <Typography variant="subtitle2">Deal & Sourcing</Typography>
          <AddDealSourcingButton
            companyId={companyIds.length == 1 ? companyIds[0] : null}
            handleOpenDealForm={() =>
              dealModal.show({
                formType: "create",
                initialValues: {
                  company: companyIds.length == 1 ? companyIds[0] : null,
                },
              })
            }
            handleOpenSourcingForm={() =>
              sourcingModal.show({
                formType: "create",
                initialValues: {
                  company: companyIds.length == 1 ? companyIds[0] : null,
                },
              })
            }
          />
        </Stack>
        {companyIds.length === 0 ? (
          <Box display={"flex"} alignItems={"center"}>
            <InfoIcon color="secondary" sx={{ scale: "0.8", mr: 1 }} />
            <Typography variant="caption" sx={{ color: "secondary.main" }}>
              Add a company to this note to see deals and sourcings
            </Typography>
          </Box>
        ) : (
          <CompanyDealsAndSourcingsTab
            state={state}
            handleChange={handleChange}
            companies={companyIds}
            onlyActiveDealsAndSourcings={onlyActiveDealsAndSourcings}
            widgetMode={widgetMode}
          />
        )}
        {!widgetMode && object?.type === "Sourcing" && (
          <SourcingInfo sourcingId={object.id} />
        )}
        {!widgetMode && object?.type === "Deal" && (
          <DealInfo dealId={object.id} />
        )}
      </Paper>
    </>
  );
};

export default CompanyDealsAndSourcings;
